import Bowser from 'bowser';
import { marketingAnalytics } from './index';
import { txLanguages } from '../../helpers/translations-helper';

const LANDING_PATH_KEY = 'pws_landing_path';
const UTM_SUPER_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term'
];

/** Live Placement inventory from Confluence 1. PWS — SA trial CTAs only. */
const TRIAL_CTA_PLACEMENTS = new Set([
  'home-hero-cta',
  'home-accomplishments-cta',
  'home-footer-cta',
  'subnav-sa-cta',
  'subnav-pricing-sa-cta',
  'sa-overview-hero-cta',
  'sa-overview-album-cta',
  'sa-overview-design-cta',
  'sa-overview-perfect-cta',
  'sa-overview-steps-cta',
  'sa-pricing-cta',
  'whatsnew-more-description-link',
  'blog-banner-fw-button',
  'post-banner-button'
]);

let clickBound = false;
let supersRegistered = false;
let downloadStartedBound = false;

function gaEnabled() {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
}

function pathWithoutLang(pathname) {
  const segments = pathname.split('/');
  const maybeLang = segments[1];
  if (txLanguages.includes(maybeLang)) {
    const rest = '/' + segments.slice(2).join('/');
    return rest === '/' ? '/' : rest.replace(/\/$/, '') || '/';
  }
  return pathname.replace(/\/$/, '') || '/';
}

function localeFromPath(pathname) {
  const maybeLang = pathname.split('/')[1];
  if (txLanguages.includes(maybeLang)) {
    return maybeLang.split('-')[0];
  }
  return 'en';
}

function deviceFamily() {
  try {
    const parsed = Bowser.parse(window.navigator.userAgent);
    const type = parsed.platform?.type;
    if (type === 'mobile') return 'Mobile';
    if (type === 'tablet') return 'Tablet';
    return 'Desktop';
  } catch (e) {
    return 'Desktop';
  }
}

function osName() {
  try {
    const name = Bowser.parse(window.navigator.userAgent).os?.name;
    if (!name) return undefined;
    const lower = name.toLowerCase();
    if (lower.includes('mac')) return 'Mac';
    if (lower.includes('windows')) return 'Windows';
    if (lower.includes('ios')) return 'Ios';
    if (lower.includes('android')) return 'Android';
    return undefined;
  } catch (e) {
    return undefined;
  }
}

function firstTouchUtms() {
  const query = new URLSearchParams(window.location.search);
  const props = {};
  UTM_SUPER_KEYS.forEach((key) => {
    if (query.has(key)) props[key] = query.get(key);
  });
  return props;
}

function landingPath() {
  try {
    const existing = sessionStorage.getItem(LANDING_PATH_KEY);
    if (existing) return existing;
    const path = pathWithoutLang(window.location.pathname);
    sessionStorage.setItem(LANDING_PATH_KEY, path);
    return path;
  } catch (e) {
    return pathWithoutLang(window.location.pathname);
  }
}

function isTrialDestination(href) {
  if (!href || href.startsWith('#')) return false;
  try {
    const url = new URL(href, window.location.origin);
    const path = url.pathname.replace(/\/$/, '') || '/';
    return (
      path.includes('/smartalbums/download') ||
      path === '/try' ||
      path.endsWith('/try') ||
      path.includes('/try/')
    );
  } catch (e) {
    return false;
  }
}

function isDownloadConfirmationPath(pathname) {
  const path = pathWithoutLang(pathname);
  return (
    path === '/smartalbums/download/mac' ||
    path === '/smartalbums/download/windows' ||
    path === '/smartalbums/download/mobile'
  );
}

function registerSupersOnce() {
  if (supersRegistered) return;
  supersRegistered = true;

  const traits = {
    Product: 'PWS',
    Path: 'Trial',
    Locale: localeFromPath(window.location.pathname),
    'Device family': deviceFamily(),
    'Landing path': landingPath(),
    ...firstTouchUtms()
  };

  const os = osName();
  if (os) traits.Os = os;

  marketingAnalytics.identify(traits);
}

function trackPageViewed() {
  registerSupersOnce();
  marketingAnalytics.track('M: Page Viewed', {
    'Page path': pathWithoutLang(window.location.pathname)
  });
}

function trackDownloadConfirmationViewed() {
  if (!gaEnabled()) return;
  registerSupersOnce();
  marketingAnalytics.track('M: Download Confirmation Viewed');
}

/**
 * @param {'Auto iframe' | 'Problems downloading' | 'Manual link'} trigger
 */
export function trackTrialDownloadStarted(trigger) {
  if (!gaEnabled()) return;
  registerSupersOnce();
  marketingAnalytics.track('M: Trial Download Started', { Trigger: trigger });
}

/** `/try/` form — no email in payload (GDPR). */
export function trackTryLeadSubmitted() {
  if (!gaEnabled()) return;
  registerSupersOnce();
  marketingAnalytics.track('M: Try Lead Submitted');
}

/** `/smartalbums/download/mobile/` (and legacy mobile follow) — Path A2. */
export function trackMobileLeadSubmitted() {
  if (!gaEnabled()) return;
  registerSupersOnce();
  marketingAnalytics.track('M: Mobile Lead Submitted');
}

function onDocumentClick(event) {
  const target = event.target?.closest?.('[data-qa]');
  if (!target) return;

  const placement = target.getAttribute('data-qa');
  if (!TRIAL_CTA_PLACEMENTS.has(placement)) return;

  const href = target.getAttribute('href');
  if (!href || !isTrialDestination(href)) return;

  registerSupersOnce();
  marketingAnalytics.track('M: Trial CTA Clicked', { Placement: placement });
}

function bindDownloadRetryClicks() {
  if (downloadStartedBound) return;
  downloadStartedBound = true;

  document.addEventListener(
    'click',
    (event) => {
      const link = event.target?.closest?.('a[target="download_iframe"]');
      if (!link) return;
      // Hidden auto-download anchor is started from JS, not user click.
      if (link.classList.contains('sa-download__hero__content__auto-download')) return;
      trackTrialDownloadStarted('Problems downloading');
    },
    true
  );
}

/**
 * Route-update hook: supers + page view + download confirmation + CTA listener.
 * Runs when gtag is present (all envs with gaId). M: events go through
 * marketingAnalytics (GA4 gtag + PostHog when loaded) — not RudderStack/Mixpanel.
 */
export function trackMarketingRoute() {
  if (!gaEnabled()) return;

  trackPageViewed();

  if (isDownloadConfirmationPath(window.location.pathname)) {
    trackDownloadConfirmationViewed();
  }

  if (!clickBound) {
    clickBound = true;
    document.addEventListener('click', onDocumentClick, true);
  }

  bindDownloadRetryClicks();
}
